import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const input = "mt-2 w-full border border-black/20 bg-white px-4 py-3 text-base outline-none transition-colors focus:border-[#FF5A00]";

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-700">{label}</span>
    <input className={input} {...props} />
    {hint && <span className="mt-1.5 block text-xs text-zinc-600">{hint}</span>}
  </label>
);

export default function AdminAccount() {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword && newPassword !== confirm) { setError("The new passwords don't match"); return; }
    const body = { current_password: current };
    if (email.trim().toLowerCase() !== user?.email) body.email = email.trim();
    if (newPassword) body.new_password = newPassword;
    if (!body.email && !body.new_password) { setError("Change the email or enter a new password first"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/account", body);
      setUser(data);
      setEmail(data.email);
      setNewPassword(""); setConfirm(""); setCurrent("");
      toast.success("Login details updated");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell eyebrow={`Signed in as ${user?.email ?? ""}`} title="Account">
      <form onSubmit={submit} className="max-w-xl space-y-6" data-testid="admin-account-form">
        <Field label="Login email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email" autoComplete="username" />
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="New password" type="password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="account-new-password" autoComplete="new-password" hint="At least 8 characters. Leave empty to keep it." />
          <Field label="Repeat new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="account-confirm-password" autoComplete="new-password" />
        </div>
        <div className="border-t border-black/10 pt-6">
          <Field label="Current password *" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="account-current-password" autoComplete="current-password" hint="Needed to confirm it's you. Other devices will be signed out after a change." />
        </div>
        {error && <p data-testid="account-error" className="border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <button type="submit" disabled={saving} data-testid="account-save-btn" className="flex items-center gap-2 bg-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white hover:bg-[#FF5A00] disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
        </button>
      </form>
    </AdminShell>
  );
}
